import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";

/**
 * Lightweight "Install app" banner. Uses the standard `beforeinstallprompt` event
 * on Chrome/Edge/Android. On iOS Safari, shows manual instructions because
 * Apple doesn't expose a programmatic install prompt.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    // Already dismissed in this session?
    if (window.sessionStorage.getItem("fia_install_dismissed") === "1") return;
    // Already installed?
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return; // iOS

    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Show after a small delay so it doesn't fight the hero
      setTimeout(() => setShow(true), 2000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS fallback: show tip after 4s on first visit
    if (isIOS) {
      const t = setTimeout(() => { setIosTip(true); setShow(true); }, 4000);
      return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setShow(false);
    window.sessionStorage.setItem("fia_install_dismissed", "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      console.info("[PWA] user accepted install");
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-[380px] z-[60] card-surface p-4 animate-fade-up"
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex w-10 h-10 rounded-full bg-forest text-white items-center justify-center shrink-0">
          <Smartphone size={18} />
        </span>
        <div className="flex-1">
          <div className="font-heading text-base tracking-tight">Install Full Indoor Air</div>
          {iosTip ? (
            <p className="text-xs text-muted2 mt-1">
              Tap the <span className="font-medium">Share</span> button in Safari, then <span className="font-medium">"Add to Home Screen"</span> to install the app.
            </p>
          ) : (
            <p className="text-xs text-muted2 mt-1">
              Get faster booking and instant offline access to your confirmation.
            </p>
          )}
          {deferred && !iosTip && (
            <button onClick={install} className="btn-primary text-xs mt-3" data-testid="install-confirm">
              Install app
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-muted2 hover:text-ink shrink-0"
          aria-label="Dismiss install prompt"
          data-testid="install-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
